import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { reimbursementService, Reimbursement, CreateReimbursementData } from '../services/reimbursementService';
import { employeeService } from '../services/employeeService';
import { Employee } from '../types';
import { useAuth } from '../context/AuthContext';

interface ReimbursementEntry {
  srNo: number;
  employeeId: string;
  expenseFor: string;
  amountINR: string;
  amountEUR: string;
  totalCostINR: string;
  remarks: string;
  invoice: File | null;
  paymentScreenshot: File | null;
  paidByEmployeeId: string;
}

export function ReimbursementPage() {
  useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entries, setEntries] = useState<ReimbursementEntry[]>([
    {
      srNo: 1,
      employeeId: '',
      expenseFor: '',
      amountINR: '',
      amountEUR: '',
      totalCostINR: '',
      remarks: '',
      invoice: null,
      paymentScreenshot: null,
      paidByEmployeeId: '',
    },
  ]);
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterEmployeeId, setFilterEmployeeId] = useState<string>('all');
  const [filterMonth, setFilterMonth] = useState<string>(String(new Date().getMonth() + 1));
  const [filterYear, setFilterYear] = useState<string>(String(new Date().getFullYear()));

  useEffect(() => {
    fetchEmployees();
    fetchReimbursements();
  }, []);

  useEffect(() => {
    fetchReimbursements();
  }, [filterStatus, filterEmployeeId, filterMonth, filterYear]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const allEmployees: Employee[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await employeeService.getAll(page, 1000, { isActive: true });
        allEmployees.push(...response.data);

        if (page >= (response.pagination?.totalPages || 1)) {
          hasMore = false;
        } else {
          page++;
        }
      }

      setEmployees(allEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchReimbursements = async () => {
    try {
      const filters: any = {};
      if (filterStatus !== 'all') {
        filters.status = filterStatus;
      }
      if (filterEmployeeId !== 'all') {
        filters.employeeId = parseInt(filterEmployeeId);
      }
      const response = await reimbursementService.getAll(filters);
      let filteredData = response.data;
      
      // Filter by month and year
      if (filterMonth && filterYear) {
        const month = parseInt(filterMonth);
        const year = parseInt(filterYear);
        filteredData = filteredData.filter((r: Reimbursement) => {
          const date = new Date(r.createdAt);
          return date.getMonth() + 1 === month && date.getFullYear() === year;
        });
      }
      
      setReimbursements(filteredData);
    } catch (error) {
      console.error('Error fetching reimbursements:', error);
      toast.error('Failed to load reimbursements');
    }
  };

  const addEntry = () => {
    setEntries([
      ...entries,
      {
        srNo: entries.length + 1,
        employeeId: '',
        expenseFor: '',
        amountINR: '',
        amountEUR: '',
        totalCostINR: '',
        remarks: '',
        invoice: null,
        paymentScreenshot: null,
        paidByEmployeeId: '',
      },
    ]);
  };

  const removeEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    // Re-number entries
    newEntries.forEach((entry, i) => {
      entry.srNo = i + 1;
    });
    setEntries(newEntries);
  };

  const updateEntry = (index: number, field: keyof ReimbursementEntry, value: any) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    // Auto-calculate totalCostINR if both INR and EUR are provided
    if (field === 'amountINR' || field === 'amountEUR') {
      const inr = parseFloat(newEntries[index].amountINR) || 0;
      const eur = parseFloat(newEntries[index].amountEUR) || 0;
      // Assuming 1 EUR = 90 INR (you can make this configurable)
      const eurToInr = eur * 90;
      const total = inr + eurToInr;
      newEntries[index].totalCostINR = total > 0 ? total.toFixed(2) : '';
    }
    
    setEntries(newEntries);
  };

  const handleFileChange = (index: number, field: 'invoice' | 'paymentScreenshot', file: File | null) => {
    const newEntries = [...entries];
    newEntries[index][field] = file;
    setEntries(newEntries);
  };

  const handleSubmit = async () => {
    // Validate entries
    for (const entry of entries) {
      if (!entry.employeeId) {
        toast.error(`Please select employee (expense for) for entry ${entry.srNo}`);
        return;
      }
      if (!entry.expenseFor.trim()) {
        toast.error(`Please enter expense description for entry ${entry.srNo}`);
        return;
      }
      if (!entry.totalCostINR || parseFloat(entry.totalCostINR) <= 0) {
        toast.error(`Please enter valid total cost for entry ${entry.srNo}`);
        return;
      }
      if (!entry.paidByEmployeeId) {
        toast.error(`Please select who paid for entry ${entry.srNo}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const entry of entries) {
        try {
          const data: CreateReimbursementData = {
            employeeId: parseInt(entry.employeeId),
            paidByEmployeeId: parseInt(entry.paidByEmployeeId),
            expenseFor: entry.expenseFor,
            amountINR: entry.amountINR ? parseFloat(entry.amountINR) : undefined,
            amountEUR: entry.amountEUR ? parseFloat(entry.amountEUR) : undefined,
            totalCostINR: parseFloat(entry.totalCostINR),
            remarks: entry.remarks || undefined,
            invoice: entry.invoice || undefined,
            paymentScreenshot: entry.paymentScreenshot || undefined,
          };

          await reimbursementService.create(data);
          successCount++;
        } catch (error: any) {
          console.error(`Error creating reimbursement entry ${entry.srNo}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} reimbursement(s)`);
        // Reset form
        setEntries([
          {
            srNo: 1,
            employeeId: '',
            expenseFor: '',
            amountINR: '',
            amountEUR: '',
            totalCostINR: '',
            remarks: '',
            invoice: null,
            paymentScreenshot: null,
            paidByEmployeeId: '',
          },
        ]);
        fetchReimbursements();
      }
      if (errorCount > 0) {
        toast.error(`Failed to create ${errorCount} reimbursement(s)`);
      }
    } catch (error) {
      console.error('Error submitting reimbursements:', error);
      toast.error('Failed to submit reimbursements');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await reimbursementService.update(id, { status });
      toast.success(`Reimbursement ${status.toLowerCase()} successfully`);
      fetchReimbursements();
    } catch (error) {
      console.error('Error updating reimbursement:', error);
      toast.error('Failed to update reimbursement');
    }
  };

  const getFileUrl = (path: string | null) => {
    if (!path) return null;
    // If it's already a full URL (GCS), return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Otherwise, assume local file
    return `${import.meta.env.VITE_API_URL || '/api'}/uploads/${path}`;
  };

  const exportToExcel = async () => {
    if (reimbursements.length === 0) {
      toast.error('No reimbursements to export');
      return;
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[parseInt(filterMonth) - 1];
    const year = filterYear;

    // Prepare data for Excel
    const excelData = reimbursements.map((r, index) => ({
      'Sr No': index + 1,
      'Employee Name': `${r.employee.firstName} ${r.employee.lastName}`,
      'Employee Code': r.employee.employeeCode,
      'Expense For': r.expenseFor,
      'Amount (INR)': r.amountINR || 0,
      'Amount (EUR)': r.amountEUR || 0,
      'Total Cost (INR)': r.totalCostINR,
      'Paid By': `${r.paidByEmployee.firstName} ${r.paidByEmployee.lastName}`,
      'Status': r.status,
      'Remarks': r.remarks || '',
      'Date': new Date(r.createdAt).toLocaleDateString('en-GB'),
    }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reimbursements');

    const headers = Object.keys(excelData[0]);
    worksheet.addRow(headers);
    worksheet.getRow(1).font = { bold: true };
    excelData.forEach((row) => worksheet.addRow(Object.values(row)));

    const colWidths = [8, 20, 15, 30, 15, 15, 18, 20, 12, 30, 12];
    colWidths.forEach((w, i) => { worksheet.getColumn(i + 1).width = w; });

    const fileName = `Reimbursements_${monthName}_${year}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${reimbursements.length} reimbursements to Excel`);
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reimbursement Management</h1>
          <p className="text-gray-500 mt-1">Upload and manage employee reimbursements</p>
        </div>
      </div>

      {/* Create Reimbursement Form */}
      <Card>
        <CardHeader title="Create Reimbursement" />
        <div className="p-6 space-y-6">
          {/* Reimbursement Entries Table */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Expense Entries</h3>
              <Button variant="outline" size="sm" onClick={addEntry}>
                + Add Entry
              </Button>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Sr No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[250px]">
                      Employee (Expense For)
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[200px]">
                      Expense Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                      INR
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                      EUR
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[150px]">
                      Total Cost (INR)
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[200px]">
                      Remarks
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[150px]">
                      Invoice
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[180px]">
                      Payment Screenshot
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[180px]">
                      Paid By
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[100px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {entry.srNo}
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Select
                          value={entry.employeeId}
                          onChange={(e) => updateEntry(index, 'employeeId', e.target.value)}
                          options={[
                            { value: '', label: 'Select Employee...' },
                            ...employees.map((emp) => ({
                              value: String(emp.id),
                              label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                            })),
                          ]}
                          className="w-full"
                          required
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Input
                          value={entry.expenseFor}
                          onChange={(e) => updateEntry(index, 'expenseFor', e.target.value)}
                          placeholder="Enter expense description"
                          className="w-full"
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Input
                          type="number"
                          step="0.01"
                          value={entry.amountINR}
                          onChange={(e) => updateEntry(index, 'amountINR', e.target.value)}
                          placeholder="0.00"
                          className="w-full"
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Input
                          type="number"
                          step="0.01"
                          value={entry.amountEUR}
                          onChange={(e) => updateEntry(index, 'amountEUR', e.target.value)}
                          placeholder="0.00"
                          className="w-full"
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Input
                          type="number"
                          step="0.01"
                          value={entry.totalCostINR}
                          onChange={(e) => updateEntry(index, 'totalCostINR', e.target.value)}
                          placeholder="0.00"
                          className="w-full font-semibold"
                          required
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Input
                          value={entry.remarks}
                          onChange={(e) => updateEntry(index, 'remarks', e.target.value)}
                          placeholder="Enter remarks"
                          className="w-full"
                        />
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <div className="space-y-1">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(index, 'invoice', e.target.files?.[0] || null)}
                            className="text-sm w-full"
                            id={`invoice-${index}`}
                          />
                          {entry.invoice && (
                            <p className="text-xs text-gray-600 mt-1 truncate" title={entry.invoice.name}>
                              {entry.invoice.name}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <div className="space-y-1">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(index, 'paymentScreenshot', e.target.files?.[0] || null)}
                            className="text-sm w-full"
                            id={`screenshot-${index}`}
                          />
                          {entry.paymentScreenshot && (
                            <p className="text-xs text-gray-600 mt-1 truncate" title={entry.paymentScreenshot.name}>
                              {entry.paymentScreenshot.name}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <Select
                          value={entry.paidByEmployeeId}
                          onChange={(e) => updateEntry(index, 'paidByEmployeeId', e.target.value)}
                          options={[
                            { value: '', label: 'Select Employee...' },
                            ...employees.map((emp) => ({
                              value: String(emp.id),
                              label: `${emp.firstName} ${emp.lastName}`,
                            })),
                          ]}
                          className="w-full"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {entries.length > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeEntry(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button onClick={handleSubmit} isLoading={submitting} disabled={submitting}>
              Submit Reimbursement(s)
            </Button>
          </div>
        </div>
      </Card>

      {/* Reimbursements List */}
      <Card>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <CardHeader title="All Reimbursements" />
          <Button variant="outline" onClick={exportToExcel} className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to Excel
          </Button>
        </div>
        <div className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Select
              label="Month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              options={[
                { value: '1', label: 'January' },
                { value: '2', label: 'February' },
                { value: '3', label: 'March' },
                { value: '4', label: 'April' },
                { value: '5', label: 'May' },
                { value: '6', label: 'June' },
                { value: '7', label: 'July' },
                { value: '8', label: 'August' },
                { value: '9', label: 'September' },
                { value: '10', label: 'October' },
                { value: '11', label: 'November' },
                { value: '12', label: 'December' },
              ]}
            />
            <Select
              label="Year"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              options={[
                { value: String(new Date().getFullYear() + 1), label: String(new Date().getFullYear() + 1) },
                { value: String(new Date().getFullYear()), label: String(new Date().getFullYear()) },
                { value: String(new Date().getFullYear() - 1), label: String(new Date().getFullYear() - 1) },
                { value: String(new Date().getFullYear() - 2), label: String(new Date().getFullYear() - 2) },
              ]}
            />
            <Select
              label="Filter by Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'PAID', label: 'Paid' },
              ]}
            />
            <Select
              label="Filter by Employee"
              value={filterEmployeeId}
              onChange={(e) => setFilterEmployeeId(e.target.value)}
              options={[
                { value: 'all', label: 'All Employees' },
                ...employees.map((emp) => ({
                  value: String(emp.id),
                  label: `${emp.firstName} ${emp.lastName}`,
                })),
              ]}
            />
          </div>

          {/* Reimbursements Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : reimbursements.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No reimbursements found</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Sr No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[200px]">
                      Employee
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[250px]">
                      Expense For
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                      Amount (INR)
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[180px]">
                      Paid By
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[150px]">
                      Attachments
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reimbursements.map((reimbursement, index) => (
                    <tr key={reimbursement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <div className="text-sm font-semibold text-gray-900">
                          {reimbursement.employee.firstName} {reimbursement.employee.lastName}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{reimbursement.employee.employeeCode}</div>
                      </td>
                      <td className="px-6 py-4 border-r border-gray-200">
                        <div className="text-sm font-medium text-gray-900">{reimbursement.expenseFor}</div>
                        {reimbursement.remarks && (
                          <div className="text-xs text-gray-500 mt-1">{reimbursement.remarks}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 border-r border-gray-200">
                        ₹{reimbursement.totalCostINR.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                        <div className="text-sm font-medium text-gray-900">
                          {reimbursement.paidByEmployee.firstName} {reimbursement.paidByEmployee.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            reimbursement.status === 'APPROVED'
                              ? 'bg-green-100 text-green-800'
                              : reimbursement.status === 'REJECTED'
                              ? 'bg-red-100 text-red-800'
                              : reimbursement.status === 'PAID'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {reimbursement.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                        <div className="flex flex-col gap-1">
                          {reimbursement.invoicePath && (
                            <a
                              href={getFileUrl(reimbursement.invoicePath)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 text-sm font-medium underline"
                            >
                              📄 Invoice
                            </a>
                          )}
                          {reimbursement.paymentScreenshotPath && (
                            <a
                              href={getFileUrl(reimbursement.paymentScreenshotPath)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 text-sm font-medium underline"
                            >
                              📷 Screenshot
                            </a>
                          )}
                          {!reimbursement.invoicePath && !reimbursement.paymentScreenshotPath && (
                            <span className="text-xs text-gray-400">No attachments</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {reimbursement.status === 'PENDING' && (
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusUpdate(reimbursement.id, 'APPROVED')}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusUpdate(reimbursement.id, 'REJECTED')}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                        {reimbursement.status === 'APPROVED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusUpdate(reimbursement.id, 'PAID')}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            Mark as Paid
                          </Button>
                        )}
                        {(reimbursement.status === 'REJECTED' || reimbursement.status === 'PAID') && (
                          <span className="text-xs text-gray-400">No actions</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
